/**
 * UpdateFlowContext - Unified update flow orchestrator
 *
 * 1. Optional offer (user confirms before prepare → clear → restart)
 * 2. Phase A: Prepare → Clear data → Restart
 * 3. Phase B: After restart → Sync → Complete
 */

import React from 'react';
import { PWA_VERSION } from '../pwa-version';
import { clearOfflineDataForPWAUpdate } from '../services/offline/clear-offline-data-for-update';
import { PWA_UPDATE_AVAILABLE } from '../services/pwa-events';
import { applyServiceWorkerUpdate } from '../utils/apply-service-worker-update';
import { debugDebug, debugWarn } from '../utils/debug';
import {
  readPersistedUpdateFlowState,
  writePersistedUpdateFlowSyncing,
  clearPersistedUpdateFlowState,
} from '../utils/update-flow-persist';
import {
  clearUpdateOfferSnooze,
  defaultSnoozeAfterDismissServiceWorker,
  defaultSnoozeAfterDismissVersion,
  isServiceWorkerOfferSnoozed,
  isVersionOfferSnoozed,
} from '../utils/update-offer-snooze';

const UPDATE_CHANNEL_NAME = 'update-sync';

const INSTALLED_VERSION_KEY = 'pwa_installed_version';
const SW_MANUAL_UPDATE_THROTTLE_MS = 60 * 60 * 1000;

export type UpdateFlowPhase =
  | 'idle'
  | 'preparing'
  | 'clearing'
  | 'restarting'
  | 'syncing'
  | 'complete';

export type UpdateOfferReason = 'version' | 'service-worker';

export interface UpdateOffer {
  reason: UpdateOfferReason;
  fromVersion: string | null;
  toVersion: string;
}

export interface UpdateFlowState {
  phase: UpdateFlowPhase;
  fromVersion: string | null;
  toVersion: string;
  progress: number;
  message: string;
}

interface UpdateFlowContextValue extends UpdateFlowState {
  /** User must confirm before startUpdateFlow runs. */
  activeOffer: UpdateOffer | null;
  /** True while preparing…restarting or post-reload syncing (not offer). */
  isUpdateInProgress: boolean;
  /** Block offline sync when SW waiting, offer shown, or flow running. */
  shouldBlockOfflineSync: boolean;
  /** Service worker has a waiting worker (may be snoozed / no offer UI). */
  serviceWorkerWaiting: boolean;
  offerUpdate: (offer: UpdateOffer) => void;
  /** Re-show offer after dismiss or open from compact CTA (clears snooze). */
  requestUpdateOffer: (offer: UpdateOffer) => void;
  dismissUpdateOffer: () => void;
  acceptUpdateAndStart: () => Promise<void>;
  startUpdateFlow: () => Promise<void>;
  completeSync: () => void;
  setSyncProgress: (progress: number, message: string) => void;
}

const initialState: UpdateFlowState = {
  phase: 'idle',
  fromVersion: null,
  toVersion: PWA_VERSION,
  progress: 0,
  message: '',
};

const UpdateFlowContext = React.createContext<UpdateFlowContextValue>({
  ...initialState,
  activeOffer: null,
  isUpdateInProgress: false,
  shouldBlockOfflineSync: false,
  serviceWorkerWaiting: false,
  offerUpdate: () => {},
  requestUpdateOffer: () => {},
  dismissUpdateOffer: () => {},
  acceptUpdateAndStart: async () => {},
  startUpdateFlow: async () => {},
  completeSync: () => {},
  setSyncProgress: () => {},
});

async function getWaitingScriptUrl(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    return reg?.waiting?.scriptURL ?? null;
  } catch {
    return null;
  }
}

export function UpdateFlowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<UpdateFlowState>(initialState);
  const [activeOffer, setActiveOffer] = React.useState<UpdateOffer | null>(null);
  const activeOfferRef = React.useRef<UpdateOffer | null>(null);
  activeOfferRef.current = activeOffer;
  const [serviceWorkerWaiting, setServiceWorkerWaiting] = React.useState(false);
  const updateInProgressRef = React.useRef(false);
  const lastSwManualUpdateCheck = React.useRef(0);

  // Ascolta UPDATE_RELOAD dagli altri tab: se un altro tab ha avviato l'update,
  // questo tab ricarica silenziosamente senza ripetere il clear-cache flow.
  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(UPDATE_CHANNEL_NAME);
    ch.onmessage = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === 'UPDATE_RELOAD' && !updateInProgressRef.current) {
        window.location.reload();
      }
    };
    return () => ch.close();
  }, []);

  const refreshSwWaiting = React.useCallback((): void => {
    if (!('serviceWorker' in navigator)) {
      setServiceWorkerWaiting(false);
      return;
    }
    void navigator.serviceWorker.getRegistration().then((reg) => {
      setServiceWorkerWaiting(!!reg?.waiting);
    });
  }, []);

  React.useEffect(() => {
    const persisted = readPersistedUpdateFlowState();
    if (persisted) {
      debugDebug(`[UpdateFlow] Resuming after restart: ${persisted.fromVersion} → ${persisted.toVersion}`);
      setState({
        phase: 'syncing',
        fromVersion: persisted.fromVersion,
        toVersion: persisted.toVersion,
        progress: 0,
        message: 'Sincronizzazione dati...',
      });
      updateInProgressRef.current = true;
    }
  }, []);

  React.useEffect(() => {
    if (state.phase === 'syncing' && readPersistedUpdateFlowState()) {
      return undefined;
    }
    refreshSwWaiting();
    const id = window.setInterval(refreshSwWaiting, 4000);
    const onSwEvent = (): void => {
      refreshSwWaiting();
    };
    window.addEventListener(PWA_UPDATE_AVAILABLE, onSwEvent);

    const onVisible = (): void => {
      if (document.visibilityState !== 'visible' || !('serviceWorker' in navigator)) return;
      const now = Date.now();
      if (now - lastSwManualUpdateCheck.current >= SW_MANUAL_UPDATE_THROTTLE_MS) {
        lastSwManualUpdateCheck.current = now;
        void navigator.serviceWorker.ready.then((r) => r.update()).catch(() => {});
      }
      refreshSwWaiting();
    };
    document.addEventListener('visibilitychange', onVisible);

    const onPageShow = (e: PageTransitionEvent): void => {
      if (!e.persisted) return;
      refreshSwWaiting();
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      clearInterval(id);
      window.removeEventListener(PWA_UPDATE_AVAILABLE, onSwEvent);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [state.phase, refreshSwWaiting]);

  const checkForUpdates = React.useCallback(async (): Promise<{
    hasUpdate: boolean;
    fromVersion: string | null;
    hasSWUpdate: boolean;
  }> => {
    const installedVersion = localStorage.getItem(INSTALLED_VERSION_KEY);
    const versionMismatch = installedVersion !== null && installedVersion !== PWA_VERSION;

    let hasSWUpdate = false;
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      hasSWUpdate = !!reg?.waiting;
    }

    return {
      hasUpdate: versionMismatch || hasSWUpdate,
      fromVersion: installedVersion,
      hasSWUpdate,
    };
  }, []);

  const startUpdateFlow = React.useCallback(async () => {
    if (updateInProgressRef.current) return;
    updateInProgressRef.current = true;
    setActiveOffer(null);

    const { hasUpdate, fromVersion, hasSWUpdate } = await checkForUpdates();

    if (!hasUpdate) {
      localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);
      updateInProgressRef.current = false;
      return;
    }

    debugDebug(`[UpdateFlow] Starting update: ${fromVersion} → ${PWA_VERSION}, SW=${hasSWUpdate}`);

    setState({
      phase: 'preparing',
      fromVersion,
      toVersion: PWA_VERSION,
      progress: 10,
      message: 'Preparazione aggiornamento...',
    });

    setState((s) => ({
      ...s,
      phase: 'clearing',
      progress: 30,
      message: 'Pulizia dati locali...',
    }));

    try {
      await clearOfflineDataForPWAUpdate();
    } catch (e) {
      debugWarn('[UpdateFlow] Clear data failed:', e);
    }

    if (hasSWUpdate && 'serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        await new Promise((r) => setTimeout(r, 350));
      }
    }

    writePersistedUpdateFlowSyncing(fromVersion || 'unknown', PWA_VERSION);
    localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);

    setState((s) => ({
      ...s,
      phase: 'restarting',
      progress: 50,
      message: 'Riavvio...',
    }));

    try {
      localStorage.setItem('pwa_post_update_check', '1');
    } catch {
      // ignore
    }

    // Notifica gli altri tab aperti di ricaricare (riceveranno il nuovo SW senza ripetere il clear-cache)
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const ch = new BroadcastChannel(UPDATE_CHANNEL_NAME);
        ch.postMessage({ type: 'UPDATE_RELOAD' });
        ch.close();
      }
    } catch {
      // ignore
    }

    window.location.reload();
  }, [checkForUpdates]);

  const offerUpdate = React.useCallback((offer: UpdateOffer) => {
    if (updateInProgressRef.current) return;
    if (readPersistedUpdateFlowState()) return;
    // Non mostrare dialog di aggiornamento mentre è in corso un logout
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('b2b_redirecting_to_login')) return;

    void (async () => {
      if (offer.reason === 'version') {
        if (isVersionOfferSnoozed(PWA_VERSION)) {
          debugDebug('[UpdateFlow] Version update offer snoozed');
          return;
        }
        setActiveOffer(offer);
        return;
      }
      const waitingUrl = await getWaitingScriptUrl();
      if (!waitingUrl) return;
      if (isServiceWorkerOfferSnoozed(waitingUrl)) {
        debugDebug('[UpdateFlow] SW update offer snoozed');
        return;
      }
      setActiveOffer(offer);
    })();
  }, []);

  const requestUpdateOffer = React.useCallback((offer: UpdateOffer) => {
    clearUpdateOfferSnooze();
    setActiveOffer(offer);
  }, []);

  const dismissUpdateOffer = React.useCallback(() => {
    const offer = activeOfferRef.current;
    void (async () => {
      if (offer?.reason === 'version') {
        defaultSnoozeAfterDismissVersion(offer.toVersion);
      } else {
        const waitingUrl = await getWaitingScriptUrl();
        defaultSnoozeAfterDismissServiceWorker(waitingUrl);
      }
      setActiveOffer(null);
    })();
  }, []);

  const acceptUpdateAndStart = React.useCallback(async () => {
    clearUpdateOfferSnooze();
    setActiveOffer(null);
    await startUpdateFlow();
  }, [startUpdateFlow]);

  const setSyncProgress = React.useCallback((progress: number, message: string) => {
    setState((s) => {
      if (s.phase !== 'syncing') return s;
      return {
        ...s,
        progress: 50 + progress * 0.5,
        message,
      };
    });
  }, []);

  const completeSync = React.useCallback(() => {
    clearPersistedUpdateFlowState();
    // Always stamp the installed version with the CURRENT PWA_VERSION (new code).
    // startUpdateFlow() wrote PWA_VERSION from the OLD code before the reload; after
    // the SW update the new bundle has a different PWA_VERSION, so without this line
    // UpdateFlowInit would see a stale installed version and re-offer on every load.
    localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);
    setState((s) => ({
      ...s,
      phase: 'complete',
      progress: 100,
      message: 'Aggiornamento completato',
    }));

    setTimeout(() => {
      setState(initialState);
      updateInProgressRef.current = false;
    }, 800);
  }, []);

  const isUpdateInProgress = state.phase !== 'idle' && state.phase !== 'complete';

  /** Re-offer SW update after snooze TTL (same boolean for serviceWorkerWaiting would not retrigger effects). */
  React.useEffect(() => {
    if (readPersistedUpdateFlowState()) return;
    if (state.phase !== 'idle') return;
    const id = window.setInterval(() => {
      if (activeOfferRef.current) return;
      if (updateInProgressRef.current) return;
      if (!('serviceWorker' in navigator)) return;
      void navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg?.waiting) return;
        const installed = localStorage.getItem(INSTALLED_VERSION_KEY);
        offerUpdate({
          reason: 'service-worker',
          fromVersion: installed,
          toVersion: PWA_VERSION,
        });
      });
    }, 20_000);
    return () => clearInterval(id);
  }, [state.phase, offerUpdate]);

  React.useEffect(() => {
    if (readPersistedUpdateFlowState()) return;
    if (state.phase !== 'idle') return;
    if (activeOffer) return;
    if (!serviceWorkerWaiting) return;
    const installed = localStorage.getItem(INSTALLED_VERSION_KEY);
    offerUpdate({
      reason: 'service-worker',
      fromVersion: installed,
      toVersion: PWA_VERSION,
    });
  }, [state.phase, activeOffer, serviceWorkerWaiting, offerUpdate]);

  const shouldBlockOfflineSync =
    activeOffer !== null || isUpdateInProgress || serviceWorkerWaiting;

  const value = React.useMemo<UpdateFlowContextValue>(
    () => ({
      ...state,
      activeOffer,
      isUpdateInProgress,
      shouldBlockOfflineSync,
      serviceWorkerWaiting,
      offerUpdate,
      requestUpdateOffer,
      dismissUpdateOffer,
      acceptUpdateAndStart,
      startUpdateFlow,
      completeSync,
      setSyncProgress,
    }),
    [
      state,
      activeOffer,
      isUpdateInProgress,
      shouldBlockOfflineSync,
      serviceWorkerWaiting,
      offerUpdate,
      requestUpdateOffer,
      dismissUpdateOffer,
      acceptUpdateAndStart,
      startUpdateFlow,
      completeSync,
      setSyncProgress,
    ]
  );

  return <UpdateFlowContext.Provider value={value}>{children}</UpdateFlowContext.Provider>;
}

export function useUpdateFlow(): UpdateFlowContextValue {
  return React.useContext(UpdateFlowContext);
}
